import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { getTimezones, getPreferredContact, getAdressTypeOption } from "api";
import errorToast from "helpers/errorToast";
import { useGetAddressesV2 } from "api/queries/company/useGetAddressesV2";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import useFeatureFlag from "hooks/useFeatureFlag";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import Radio from "components/atoms/Radio/Radio";
import testIds from "constants/testIds";
import { FEATURE_FLAGS } from "constants/featureFlags";
import FormItemInfo from "../../Common/FormItemInfo";
import SearchSelect from "../../Common/SearchSelect";
import AddressV2Display from "../PickupDeliveryAddress/AddressV2Display";
import AddressTypeSelector from "./AddressTypeSelector";
import PortForm from "./PortForm";
import { notifyPartyOption } from "../ScheduleDefaultOptions";
import { AddressOption, formatAddress } from "../PickupDeliveryAddress/helpers";
import useNotifyPartyValidation from "../hooks/useNotifyPartyValidation";
import useNotifyPartyAndConsigneeFormSynchronization from "../hooks/useNotifyPartyAndConsigneeFormSynchronization";
import useNotifyPartyAndConsigneeAddressManager from "../hooks/useNotifyPartyAndConsigneeAddressManager";

/**
 * Props interface for NotifyPartyAndConsignee component
 */
interface NotifyPartyAndConsigneeProps {
  /** Type of party - either notify party or consignee */
  partyType: "notify_party" | "consignee";
  /** Optional callback to set whether notify party is required */
  setIsNotifyPartyRequired?: (isRequired: boolean) => void;
}

NotifyPartyAndConsignee.defaultProps = {
  setIsNotifyPartyRequired: () => {},
};

/**
 * Type definition for form field values used in address management
 * Using any to maintain compatibility with existing API response structure
 */

const SEARCH_DEBOUNCE_DELAY = 500;
const PORT_SEARCH_DEBOUNCE_DELAY = 300;

/**
 * NotifyPartyAndConsignee Component
 *
 * This component handles the form interface for both notify party and consignee information
 * in a transportation management system. It provides address selection, contact management,
 * and synchronization with delivery address information.
 *
 * Features:
 * - Address search and selection with auto-complete
 * - Port/airport selection for different transport modes
 * - Contact information management with preferred contact fetching
 * - Synchronization options with delivery address and consignee information
 * - Dynamic validation based on field completion
 *
 * @param partyType - Determines if this is for notify party or consignee
 * @param setIsNotifyPartyRequired - Optional callback to update parent about notify party requirement
 */
export default function NotifyPartyAndConsignee({ partyType, setIsNotifyPartyRequired }: NotifyPartyAndConsigneeProps) {
  const intl = useIntl();
  const { getValues, setValue, watch, trigger, clearErrors } = useFormContext();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  // State management
  const [searchInput, setSearchInput] = useState("");
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [search, setSearch] = useState("");
  const [portAddressOption, setPortAddressOption] = useState([]);
  const [disableAllFields, setDisableAllFields] = useState(false);

  // Refs for cleanup
  const portRequestAbortController = useRef<AbortController | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  // Custom hooks
  const { setAddressValues, resetAddressValues, resetContactValues, resetPortValues } =
    useNotifyPartyAndConsigneeAddressManager(partyType);
  const { overwriteAddressValuesWithDelivery, overwriteAddressValuesWithConsignee } =
    useNotifyPartyAndConsigneeFormSynchronization(partyType);
  const isRequired = useNotifyPartyValidation(partyType, setIsNotifyPartyRequired);

  // Form field watchers
  const address_reference_number = watch(`schedule.${partyType}.address_reference_number`);
  const address_street_address = watch(`schedule.${partyType}.address_street_address`);
  const countryWatch = watch(`schedule.${partyType}.country`);
  const stateWatch = watch(`schedule.${partyType}.state`);
  const addressTypeWatch = watch(`schedule.${partyType}.address_type`);
  const isOptionalAirportOrSeaport =
    isOptionalPortAddressFormEnabled && (addressTypeWatch === "airport" || addressTypeWatch === "seaport");

  /**
   * Fetches timezone information based on country/state selection
   */
  const getTimeZones = useCallback(
    (countryChange: boolean, seaportCountryCode: string = "", preferredTimezone: string = "") => {
      const addressType = getValues(`schedule.${partyType}.address_type`);
      const isAirportOrSeaport =
        isOptionalPortAddressFormEnabled && (addressType === "airport" || addressType === "seaport");

      if (!isAirportOrSeaport && !countryWatch && !seaportCountryCode) return;

      const countryCode = getValues(`schedule.${partyType}.country_code`);
      const countryParam = getValues(`schedule.${partyType}.country`);
      const stateNameParam = getValues(`schedule.${partyType}.state`);

      const timezoneRequestConfig = isAirportOrSeaport
        ? {}
        : {
            params: {
              country_code: seaportCountryCode || countryCode || countryParam,
              ...(addressType === "address" && stateNameParam && { state_name: stateNameParam }),
            },
          };

      getTimezones(timezoneRequestConfig).then((res) => {
        const availableTimezones = res.data.map(({ value, name }: { value: string; name: string }) => ({
          label: name,
          value,
        }));
        if (countryChange && availableTimezones?.length > 0) {
          const timezoneField = `schedule.${partyType}.timezone`;
          if (!isOptionalPortAddressFormEnabled) {
            setValue(timezoneField, availableTimezones[0].value, { shouldValidate: true });
            return;
          }
          const currentTimezone = preferredTimezone || getValues(timezoneField);
          const hasCurrentTimezone = !!availableTimezones.find((timezone) => timezone.value === currentTimezone);
          const hasSourceSelection =
            addressType === "address"
              ? !!getValues(`schedule.${partyType}.address_name`)
              : !!(
                  getValues(`schedule.${partyType}.airport`) ||
                  getValues(`schedule.${partyType}.airport_name`) ||
                  getValues(`schedule.${partyType}.seaport`) ||
                  getValues(`schedule.${partyType}.seaport_name`)
                );

          // Never auto-pick a timezone when there is no selected source (address/airport/seaport).
          if (hasCurrentTimezone) {
            setValue(timezoneField, currentTimezone, { shouldValidate: true });
          } else if (hasSourceSelection) {
            setValue(timezoneField, availableTimezones[0].value, { shouldValidate: true });
          }
        }
      });
    },
    [countryWatch, isOptionalPortAddressFormEnabled, partyType, getValues, setValue],
  );

  useEffect(() => {
    const isAirportOrSeaport =
      isOptionalPortAddressFormEnabled && (addressTypeWatch === "airport" || addressTypeWatch === "seaport");
    if (isAirportOrSeaport || countryWatch || stateWatch) {
      getTimeZones(true);
    }
  }, [countryWatch, stateWatch, addressTypeWatch, getTimeZones, isOptionalPortAddressFormEnabled]);

  // API queries
  const { data: addresses = [] } = useGetAddressesV2({ params: { query: searchInput } }, { keepPreviousData: true });

  const { data: addressV2Details } = useGetAddressesV2Details(address_reference_number, {
    options: {
      enabled: Boolean(!address_street_address && address_reference_number),
      refetchOnMount: true,
    },
  });

  /**
   * Handles address search input with debouncing
   */
  const onInputChange = useCallback(
    (val: string) => {
      const formAddressContactType = getValues(`schedule.${partyType}.form_address_contact_type`);

      // Ignore synthetic typeahead updates while values are being synchronized
      // from delivery/consignee to avoid delayed reset of copied fields.
      if (disableAllFields || formAddressContactType !== "custom") return;

      if (!val?.trim()) {
        setValue(`schedule.${partyType}.address_reference_number`, "");
      }
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(() => {
        setSearchInput(val);
        resetAddressValues();
        setIsInitialRender(false);
      }, SEARCH_DEBOUNCE_DELAY);
    },
    [disableAllFields, getValues, partyType, resetAddressValues, setValue],
  );

  /**
   * Handles address selection from dropdown
   */
  const handleSelectAddress = useCallback(
    (event: any[]) => {
      const address = event[0];
      if (!address) return;

      setValue(`schedule.${partyType}.address_reference_number`, address.reference_number);
      setAddressValues(address);

      // Fetch and set preferred contact information
      getPreferredContact(address?.reference_number).then((res) => {
        setValue(`schedule.${partyType}.contact_name`, res.data.name, { shouldValidate: true });
        setValue(`schedule.${partyType}.contact_phone`, res.data.phone_number, { shouldValidate: true });
        setValue(`schedule.${partyType}.contact_email`, res.data.email, { shouldValidate: true });
      });
    },
    [partyType, setValue, setAddressValues],
  );

  /**
   * Fetches port/airport options based on search input
   */
  const handleAddressTypeOption = useCallback(() => {
    if (getValues(`schedule.${partyType}.address_type`) === "address") return;

    if (portRequestAbortController.current) {
      portRequestAbortController.current.abort();
    }

    portRequestAbortController.current = new AbortController();
    const endpoint = getValues(`schedule.${partyType}.address_type`) === "seaport" ? "/common/seaport" : "/common/airport";

    getAdressTypeOption(endpoint, search, { signal: portRequestAbortController.current?.signal })
      .then((res) => setPortAddressOption(res.data.results))
      .catch((error) => {
        if (error?.name !== "CanceledError") {
          errorToast(error);
        }
      });
  }, [partyType, search, getValues]);

  /**
   * Event handler for delivery address synchronization checkbox
   */
  const sameDeliveryAddressContactOnChange = (e: any) => {
    const syncedRequiredFields = [
      `schedule.${partyType}.address_name`,
      `schedule.${partyType}.contact_name`,
      `schedule.${partyType}.contact_phone`,
      `schedule.${partyType}.contact_email`,
      `schedule.${partyType}.timezone`,
    ];
    const isCustomAddressContact = getValues(`schedule.${partyType}.form_address_contact_type`) === "custom";
    if (e.target.checked) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      setValue(`schedule.${partyType}.form_address_contact_type`, "delivery");
      overwriteAddressValuesWithDelivery();
      setDisableAllFields(true);
      clearErrors(syncedRequiredFields);
      trigger(syncedRequiredFields);
    } else if (!isCustomAddressContact) {
      resetAddressValues();
      resetContactValues();
      resetPortValues();
      setDisableAllFields(false);
      setValue(`schedule.${partyType}.form_address_contact_type`, "custom");
    }
  };

  /**
   * Event handler for consignee/delivery address synchronization radio buttons
   */
  const sameConsigneeAddressContactOnChange = useCallback(
    (e: any) => {
      const syncedRequiredFields = [
        `schedule.${partyType}.address_name`,
        `schedule.${partyType}.contact_name`,
        `schedule.${partyType}.contact_phone`,
        `schedule.${partyType}.contact_email`,
        `schedule.${partyType}.timezone`,
      ];
      const actionMap = {
        sameAsConsigneeAddressContact: overwriteAddressValuesWithConsignee,
        sameAsDeliveryAddressContact: overwriteAddressValuesWithDelivery,
      };

      const action = actionMap[e.target.value as keyof typeof actionMap];
      if (action) {
        if (timeoutId.current) {
          clearTimeout(timeoutId.current);
        }
        setValue(
          `schedule.${partyType}.form_address_contact_type`,
          e.target.value === "sameAsConsigneeAddressContact" ? "consignee" : "delivery",
        );
        action();
        setDisableAllFields(true);
        clearErrors(syncedRequiredFields);
        trigger(syncedRequiredFields);
      }
      trigger(`schedule.${partyType}`);
    },
    [overwriteAddressValuesWithConsignee, overwriteAddressValuesWithDelivery, trigger, partyType, clearErrors, setValue],
  );

  // Effects
  useEffect(() => {
    if (addressV2Details && !address_street_address && address_reference_number) {
      setAddressValues(addressV2Details);
      trigger(`schedule.${partyType}.address_name`);
    }
  }, [addressV2Details, address_street_address, address_reference_number, partyType, setAddressValues, trigger]);

  useEffect(() => {
    if (search) {
      const timeoutId = setTimeout(handleAddressTypeOption, PORT_SEARCH_DEBOUNCE_DELAY);
      return () => clearTimeout(timeoutId);
    }
  }, [search, handleAddressTypeOption]);

  useEffect(() => {
    if (getValues(`schedule.${partyType}.same_as_address_contact`) === "customAddressContact") {
      setDisableAllFields(false);
    }
  }, [getValues(`schedule.${partyType}.same_as_address_contact`)]);

  useEffect(() => {
    const formAddressContactType = getValues(`schedule.${partyType}.form_address_contact_type`);

    // if consignee and form address contact type is delivery, set same as delivery address contact to true
    if (partyType === "consignee") {
      if (formAddressContactType === "delivery") {
        setValue(`schedule.${partyType}.same_as_delivery_address_contact`, true);
      }
    }

    if (partyType === "notify_party") {
      if (formAddressContactType === "delivery") {
        setValue(`schedule.${partyType}.same_as_address_contact`, "sameAsDeliveryAddressContact");
      } else if (formAddressContactType === "consignee") {
        setValue(`schedule.${partyType}.same_as_address_contact`, "sameAsConsigneeAddressContact");
      }
    }
  }, []);

  useEffect(() => {
    if (watch(`schedule.${partyType}.same_as_delivery_address_contact`)) {
      sameDeliveryAddressContactOnChange({ target: { checked: true } } as any);
    } else {
      sameDeliveryAddressContactOnChange({ target: { checked: false } } as any);
    }
  }, [watch(`schedule.${partyType}.same_as_delivery_address_contact`)]);

  useEffect(() => {
    if (watch(`schedule.${partyType}.same_as_address_contact`)) {
      sameConsigneeAddressContactOnChange({
        target: { value: watch(`schedule.${partyType}.same_as_address_contact`) },
      } as any);
    }
  }, [watch(`schedule.${partyType}.same_as_address_contact`)]);

  // Prepare address options for dropdown
  const options =
    addressV2Details && isInitialRender
      ? [formatAddress(addressV2Details as any)]
      : addresses?.map((address: any) => formatAddress(address));

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <div className="mr-xl" style={{ minWidth: "200px" }}>
        <FormItemInfo
          labelId={partyType === "notify_party" ? "new-request.schedule.notifyParty" : "new-request.schedule.consignee"}
          label={partyType === "notify_party" ? "Notify party" : "Consignee"}
        />
        {partyType === "notify_party" && (
          <div className="font-b3 text-secondary">
            (<FormattedMessage id="general.optional" defaultMessage="Optional" />)
          </div>
        )}
      </div>

      <div className="w-100" style={{ maxWidth: "400px" }}>
        {/* Consignee synchronization checkbox */}
        {partyType === "consignee" && (
          <div className="mb-xl">
            <Checkbox
              providerSections
              checkboxLabel={intl.formatMessage({
                id: "new-request.schedule.sameAsDeliveryAddressContact",
                defaultMessage: "Same as delivery address & delivery contact",
              })}
              name={`schedule.${partyType}.same_as_delivery_address_contact`}
              dataTestId={testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_SAME_AS_DELIVERY}
            />
          </div>
        )}

        {/* Notify party synchronization radio buttons */}
        {partyType === "notify_party" && (
          <div className="mb-xl border-bottom">
            <div data-testid={testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_SYNC}>
              <Radio
                name={`schedule.${partyType}.same_as_address_contact`}
                options={notifyPartyOption(intl)}
                className="border-0 d-flex flex-row width-fit p-none mb-xl"
                customColClass="pl-0"
                customOnChange={sameConsigneeAddressContactOnChange}
                testIdPrefix={testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_SYNC}
              />
            </div>
          </div>
        )}

        <AddressTypeSelector partyType={partyType} disabled={disableAllFields} />

        {/* Port/Airport form for non-address types */}
        {getValues(`schedule.${partyType}.address_type`) !== "address" && (
          <div className="d-flex flex-column gap-xl mb-xl">
            <PortForm
              partyType={partyType}
              portAddressOption={portAddressOption}
              getTimeZones={getTimeZones}
              setSearch={setSearch}
              disabled={disableAllFields}
              required={isRequired}
            />
          </div>
        )}

        {/* Address search and selection */}
        <SearchSelect
          name={`schedule.${partyType}.address_name`}
          testId={
            partyType === "notify_party"
              ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_ADDRESS_SELECT
              : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_ADDRESS_SELECT
          }
          preselect={false}
          enableManualValue={false}
          required={isRequired && !isOptionalAirportOrSeaport}
          disableAutoClear
          options={options}
          label={intl.formatMessage({
            id: "new-request.schedule.address",
            defaultMessage: "Address",
          })}
          placeholder={intl.formatMessage({
            id: "new-request.schedule.chooseAddressV2Placeholder",
            defaultMessage: "Search by address, location reference number, or custom reference",
          })}
          filterBy={() => true}
          handleRenderOption={({ option }: any) => <AddressOption option={option} />}
          onInputChange={onInputChange}
          onSelect={handleSelectAddress}
          disable={disableAllFields}
        />

        {/* Address display */}
        <div className="d-flex justify-content-between align-items-start mt-xs">
          <AddressV2Display
            addressNameKey={`schedule.${partyType}.address_name`}
            addressStreetKey={`schedule.${partyType}.address_street`}
            addressStreetLine2Key={`schedule.${partyType}.address_street_line2`}
            addressCityKey={`schedule.${partyType}.address_city`}
            addressStateKey={`schedule.${partyType}.address_state`}
            addressCountryKey={`schedule.${partyType}.address_country`}
            addressZipcodeKey={`schedule.${partyType}.address_zipcode`}
            unLocodeKey={`schedule.${partyType}.address_un_locode`}
            locationReferenceNumberKey={`schedule.${partyType}.address_location_reference_number`}
            customAddressReferenceKey={`schedule.${partyType}.address_custom_address_reference`}
          />
        </div>
      </div>
    </div>
  );
}
